/**
 * EnforcementSetupPage — Guided wizard for configuring access enforcement.
 *
 * Walks users through:
 *   1. Selecting their auth system (rule42-auth, Auth0, Okta, custom)
 *   2. Choosing an enforcement pattern (push/pull/passive)
 *   3. Configuring webhooks, polling, or manual triggers
 *   4. Auto-creating automation rules for the selected pattern
 *   5. Testing the integration
 *
 * @module packages/billing-admin/src/pages/EnforcementSetupPage
 * @since 2026-06-21
 */
import React, { useState, useCallback, useMemo } from 'react';
import AdminLayout from '../components/AdminLayout';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { useOrgContext } from '../contexts/OrgContext';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type AuthSystem = 'rule42-auth' | 'auth0' | 'okta' | 'custom' | 'none';
type EnforcementPattern = 'push' | 'pull' | 'passive';
type WizardStep = 'auth-system' | 'pattern' | 'configure' | 'rules' | 'test';

interface WebhookConfig {
  endpointUrl: string;
  secret: string;
  events: string[];
}

interface PollConfig {
  apiUrl: string;
  intervalMinutes: number;
  apiKeyHeader: string;
}

interface SuggestedRule {
  name: string;
  trigger: string;
  action: string;
  delay: number;
  description: string;
  selected: boolean;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const AUTH_SYSTEMS: Array<{ id: AuthSystem; label: string; icon: string; description: string }> = [
  { id: 'rule42-auth', label: 'rule42-auth', icon: '🔐', description: 'Native integration with automatic role sync, entitlement mapping, and real-time provisioning.' },
  { id: 'auth0', label: 'Auth0', icon: '🌐', description: 'Webhook-based integration via Auth0 Actions. Supports post-login hooks and management API.' },
  { id: 'okta', label: 'Okta', icon: '🏢', description: 'Event hook integration for user lifecycle events. Supports inline hooks for real-time decisions.' },
  { id: 'custom', label: 'Custom / Other', icon: '⚙️', description: 'Any OAuth2/OIDC provider or custom auth system. Configure via webhooks or API polling.' },
  { id: 'none', label: 'No Auth System', icon: '📋', description: 'Manual enforcement only. Use automation rules to send notifications when billing events occur.' },
];

const ENFORCEMENT_PATTERNS: Array<{ id: EnforcementPattern; label: string; icon: string; description: string; best_for: string }> = [
  { id: 'push', label: 'Push — Webhooks', icon: '📡', description: 'Your billing system pushes events to your auth system or app in real-time via webhooks.', best_for: 'Real-time provisioning, instant access changes' },
  { id: 'pull', label: 'Pull — API Polling', icon: '🔄', description: 'Your app checks entitlements via the billing API before granting access.', best_for: 'Feature gates, metered access, client-side checks' },
  { id: 'passive', label: 'Passive — Notifications', icon: '📬', description: 'Automation rules send emails or notifications. Access changes are handled manually.', best_for: 'Low-volume, manual workflows, early-stage products' },
];

const TRIGGER_LABELS: Record<string, string> = {
  'subscription.created': 'Subscription Created',
  'subscription.canceled': 'Subscription Canceled',
  'subscription.expired': 'Subscription Expired',
  'subscription.trial_ending': 'Trial Ending',
  'subscription.upgraded': 'Subscription Upgraded',
  'subscription.downgraded': 'Subscription Downgraded',
  'invoice.past_due': 'Invoice Past Due',
  'payment.failed': 'Payment Failed',
  'payment.succeeded': 'Payment Succeeded',
  'customer.reinstated': 'Customer Reinstated',
  'customer.suspended': 'Customer Suspended',
  'entitlements.granted': 'Entitlements Granted',
  'entitlements.revoked': 'Entitlements Revoked',
};

const WEBHOOK_EVENTS = [
  'subscription.created', 'subscription.canceled', 'subscription.expired',
  'subscription.upgraded', 'subscription.downgraded',
  'payment.failed', 'payment.succeeded',
  'customer.reinstated', 'customer.suspended',
];

// ---------------------------------------------------------------------------
// Suggested rules per pattern
// ---------------------------------------------------------------------------

function getSuggestedRules(pattern: EnforcementPattern, authSystem: AuthSystem): SuggestedRule[] {
  const base: SuggestedRule[] = [];

  if (pattern === 'push') {
    base.push(
      { name: 'Provision on Subscribe', trigger: 'subscription.created', action: 'send_webhook', delay: 0, description: 'Immediately notify your system to grant access when a subscription starts.', selected: true },
      { name: 'Deprovision on Cancel', trigger: 'subscription.canceled', action: 'send_webhook', delay: 0, description: 'Notify your system to revoke access when a subscription is canceled.', selected: true },
      { name: 'Suspend on Payment Failure', trigger: 'payment.failed', action: 'send_webhook', delay: 3, description: 'After 3 days of failed payment, notify your system to restrict access.', selected: true },
      { name: 'Restore on Payment Success', trigger: 'payment.succeeded', action: 'send_webhook', delay: 0, description: 'Re-enable access immediately when a payment succeeds.', selected: true },
      { name: 'Upgrade Entitlements', trigger: 'subscription.upgraded', action: 'send_webhook', delay: 0, description: 'Notify your system to upgrade the user\'s access tier.', selected: false },
      { name: 'Downgrade Entitlements', trigger: 'subscription.downgraded', action: 'send_webhook', delay: 0, description: 'Notify your system to downgrade the user\'s access tier.', selected: false },
    );
  } else if (pattern === 'pull') {
    base.push(
      { name: 'Grant Entitlements on Subscribe', trigger: 'subscription.created', action: 'grant_entitlements', delay: 0, description: 'Record entitlements in billing so your app can query them.', selected: true },
      { name: 'Revoke Entitlements on Cancel', trigger: 'subscription.canceled', action: 'revoke_entitlements', delay: 0, description: 'Remove entitlements so API checks deny access.', selected: true },
      { name: 'Restrict on Payment Failure', trigger: 'payment.failed', action: 'restrict_access', delay: 7, description: 'Mark account restricted after 7 days of unpaid invoices.', selected: true },
      { name: 'Restore on Reinstatement', trigger: 'customer.reinstated', action: 'grant_entitlements', delay: 0, description: 'Re-grant entitlements when customer is reinstated.', selected: true },
    );
  } else {
    base.push(
      { name: 'Email on Subscription Start', trigger: 'subscription.created', action: 'send_email', delay: 0, description: 'Send a welcome email with access instructions.', selected: true },
      { name: 'Email on Cancellation', trigger: 'subscription.canceled', action: 'send_email', delay: 0, description: 'Notify admin that access should be revoked.', selected: true },
      { name: 'Payment Reminder', trigger: 'payment.failed', action: 'send_email', delay: 3, description: 'Send a payment reminder after 3 days.', selected: true },
      { name: 'Notify External on Suspension', trigger: 'customer.suspended', action: 'notify_external', delay: 0, description: 'Send a notification to your ops channel.', selected: false },
    );
  }

  // For rule42-auth, add native sync rules
  if (authSystem === 'rule42-auth') {
    base.unshift(
      { name: 'Sync Roles on Subscribe', trigger: 'subscription.created', action: 'grant_entitlements', delay: 0, description: 'Automatically sync business roles from rule42-auth when a subscription starts.', selected: true },
    );
  }

  return base;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

function useApiBase(): string {
  const { apiBaseUrl } = useAdminAuth();
  const { activeOrg } = useOrgContext();
  return `${apiBaseUrl}/api/v1/orgs/${activeOrg?.orgId ?? 'default'}`;
}

function useAuthHeaders(): Record<string, string> {
  const { token } = useAdminAuth();
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function EnforcementSetupPage() {
  const base = useApiBase();
  const headers = useAuthHeaders();

  // Wizard state
  const [step, setStep] = useState<WizardStep>('auth-system');
  const [authSystem, setAuthSystem] = useState<AuthSystem | null>(null);
  const [pattern, setPattern] = useState<EnforcementPattern | null>(null);

  // Config state
  const [webhookConfig, setWebhookConfig] = useState<WebhookConfig>({ endpointUrl: '', secret: '', events: [...WEBHOOK_EVENTS] });
  const [pollConfig, setPollConfig] = useState<PollConfig>({ apiUrl: '', intervalMinutes: 5, apiKeyHeader: 'X-Billing-Api-Key' });

  // Rules state
  const [suggestedRules, setSuggestedRules] = useState<SuggestedRule[]>([]);
  const [creatingRules, setCreatingRules] = useState(false);
  const [createdCount, setCreatedCount] = useState(0);

  // Test state
  const [testStatus, setTestStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle');
  const [testMessage, setTestMessage] = useState('');

  // Sync state
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<{ created: number; updated: number } | null>(null);

  const steps: WizardStep[] = ['auth-system', 'pattern', 'configure', 'rules', 'test'];
  const stepIndex = steps.indexOf(step);
  const stepLabels = ['Auth System', 'Pattern', 'Configure', 'Rules', 'Verify'];

  const canProceed = useMemo(() => {
    if (step === 'auth-system') return authSystem !== null;
    if (step === 'pattern') return pattern !== null;
    if (step === 'configure') {
      if (pattern === 'push') return webhookConfig.endpointUrl.length > 0;
      return true;
    }
    return true;
  }, [step, authSystem, pattern, webhookConfig]);

  const goNext = useCallback(() => {
    const idx = steps.indexOf(step);
    if (idx < steps.length - 1) {
      const next = steps[idx + 1]!;
      // When moving to rules step, generate suggestions
      if (next === 'rules' && pattern && authSystem) {
        setSuggestedRules(getSuggestedRules(pattern, authSystem));
      }
      setStep(next);
    }
  }, [step, pattern, authSystem]);

  const goBack = useCallback(() => {
    const idx = steps.indexOf(step);
    if (idx > 0) setStep(steps[idx - 1]!);
  }, [step]);

  // Create automation rules
  const handleCreateRules = useCallback(async () => {
    const selected = suggestedRules.filter(r => r.selected);
    if (selected.length === 0) return;
    setCreatingRules(true);
    setCreatedCount(0);
    let count = 0;
    for (const rule of selected) {
      try {
        const actionConfig: Record<string, unknown> = {};
        if (rule.action === 'send_webhook' && webhookConfig.endpointUrl) {
          actionConfig.url = webhookConfig.endpointUrl;
          if (webhookConfig.secret) actionConfig.secret = webhookConfig.secret;
        }
        const res = await fetch(`${base}/automation-rules`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            name: rule.name,
            description: rule.description,
            triggerEvent: rule.trigger,
            actionType: rule.action,
            delayDays: rule.delay,
            actionConfig,
            isActive: true,
          }),
        });
        if (res.ok) count++;
      } catch { /* skip failed */ }
    }
    setCreatedCount(count);
    setCreatingRules(false);
  }, [suggestedRules, base, headers, webhookConfig]);

  // Test webhook
  const handleTestWebhook = useCallback(async () => {
    setTestStatus('testing');
    setTestMessage('');
    try {
      const res = await fetch(webhookConfig.endpointUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event: 'test.ping',
          timestamp: new Date().toISOString(),
          data: { source: 'rule42-billing', message: 'Webhook connectivity test' },
        }),
      });
      if (res.ok) {
        setTestStatus('success');
        setTestMessage(`Webhook responded with ${res.status}. Integration is working.`);
      } else {
        setTestStatus('error');
        setTestMessage(`Webhook responded with ${res.status}. Check your endpoint.`);
      }
    } catch (err: any) {
      setTestStatus('error');
      setTestMessage(`Connection failed: ${err.message}. This may be due to CORS — test from your server instead.`);
    }
  }, [webhookConfig]);

  // Sync from auth
  const handleSyncFromAuth = useCallback(async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await fetch(`${base}/entitlements/sync`, { method: 'POST', headers });
      if (res.ok) {
        const data = await res.json();
        setSyncResult(data);
      }
    } catch { /* ignore */ }
    setSyncing(false);
  }, [base, headers]);

  // Toggle rule selection
  const toggleRule = (idx: number) => {
    setSuggestedRules(prev => prev.map((r, i) => i === idx ? { ...r, selected: !r.selected } : r));
  };

  return (
    <AdminLayout title="Enforcement Setup">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* ── Progress bar ── */}
        <div className="flex items-center justify-between mb-8">
          {stepLabels.map((label, i) => (
            <div key={label} className="flex items-center flex-1">
              <div className={`flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold ${
                i < stepIndex ? 'bg-emerald-500 text-white'
                : i === stepIndex ? 'bg-indigo-600 text-white'
                : 'bg-slate-200 text-slate-600'
              }`}>
                {i < stepIndex ? '✓' : i + 1}
              </div>
              <span className={`ml-2 text-xs font-medium ${i <= stepIndex ? 'text-slate-900' : 'text-slate-500'}`}>{label}</span>
              {i < stepLabels.length - 1 && <div className={`flex-1 h-0.5 mx-3 ${i < stepIndex ? 'bg-emerald-500' : 'bg-slate-200'}`} />}
            </div>
          ))}
        </div>

        {/* ── Step 1: Auth System ── */}
        {step === 'auth-system' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-bold text-slate-900">What auth system does your application use?</h2>
              <p className="text-sm text-slate-600 mt-1">We'll configure the optimal enforcement pattern based on your auth provider.</p>
            </div>
            <div className="grid grid-cols-1 gap-3">
              {AUTH_SYSTEMS.map(sys => (
                <button
                  key={sys.id}
                  onClick={() => setAuthSystem(sys.id)}
                  className={`flex items-start gap-4 p-4 rounded-lg border text-left transition-all ${
                    authSystem === sys.id
                      ? 'border-indigo-500 bg-indigo-500/10'
                      : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                >
                  <span className="text-2xl">{sys.icon}</span>
                  <div>
                    <div className="font-semibold text-slate-900">{sys.label}</div>
                    <div className="text-xs text-slate-600 mt-0.5">{sys.description}</div>
                  </div>
                  {authSystem === sys.id && <span className="ml-auto text-indigo-600 text-lg">✓</span>}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── Step 2: Enforcement Pattern ── */}
        {step === 'pattern' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-bold text-slate-900">Choose your enforcement pattern</h2>
              <p className="text-sm text-slate-600 mt-1">
                {authSystem === 'rule42-auth'
                  ? 'rule42-auth supports all patterns natively. Push (webhooks) is recommended for real-time enforcement.'
                  : authSystem === 'none'
                  ? 'Without an auth system, passive notifications are recommended. You can enforce manually.'
                  : 'Choose how your application should react to billing events.'}
              </p>
            </div>
            <div className="grid grid-cols-1 gap-3">
              {ENFORCEMENT_PATTERNS.map(p => (
                <button
                  key={p.id}
                  onClick={() => setPattern(p.id)}
                  className={`flex items-start gap-4 p-4 rounded-lg border text-left transition-all ${
                    pattern === p.id
                      ? 'border-indigo-500 bg-indigo-500/10'
                      : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                >
                  <span className="text-2xl">{p.icon}</span>
                  <div className="flex-1">
                    <div className="font-semibold text-slate-900">{p.label}</div>
                    <div className="text-xs text-slate-600 mt-0.5">{p.description}</div>
                    <div className="text-[10px] text-indigo-600 mt-1">Best for: {p.best_for}</div>
                  </div>
                  {pattern === p.id && <span className="text-indigo-600 text-lg">✓</span>}
                  {p.id === 'push' && authSystem === 'rule42-auth' && (
                    <span className="text-[10px] bg-emerald-500/20 text-emerald-600 px-2 py-0.5 rounded-full">Recommended</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── Step 3: Configure ── */}
        {step === 'configure' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-bold text-slate-900">Configure your integration</h2>
              <p className="text-sm text-slate-600 mt-1">Set up the connection between billing and your {authSystem === 'rule42-auth' ? 'rule42-auth instance' : 'auth system'}.</p>
            </div>

            {/* rule42-auth specific */}
            {authSystem === 'rule42-auth' && (
              <div className="space-y-4">
                <div className="p-4 rounded-lg border border-emerald-500/30 bg-emerald-500/5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-lg">✅</span>
                    <span className="font-semibold text-emerald-600">Native Integration Available</span>
                  </div>
                  <p className="text-xs text-slate-600">rule42-auth is pre-configured to sync with billing. Business roles defined in your auth system automatically become entitlements here.</p>
                </div>

                <div className="p-4 rounded-lg border border-slate-200 bg-white">
                  <h3 className="text-sm font-semibold text-slate-900 mb-3">Sync Business Roles</h3>
                  <p className="text-xs text-slate-600 mb-3">Pull business roles from rule42-auth and create matching entitlement definitions.</p>
                  <button
                    onClick={() => void handleSyncFromAuth()}
                    disabled={syncing}
                    className="px-4 py-2 text-xs font-medium rounded bg-indigo-600 text-white hover:bg-indigo-500 disabled:opacity-50"
                  >
                    {syncing ? 'Syncing…' : 'Sync from Auth'}
                  </button>
                  {syncResult && (
                    <div className="mt-2 text-xs text-emerald-600">
                      Synced: {syncResult.created} created, {syncResult.updated} updated
                    </div>
                  )}
                </div>

                {pattern === 'push' && (
                  <div className="p-4 rounded-lg border border-slate-200 bg-white">
                    <h3 className="text-sm font-semibold text-slate-900 mb-3">Webhook Endpoint (Optional)</h3>
                    <p className="text-xs text-slate-600 mb-3">If you have a custom service that needs to react to billing events beyond what rule42-auth handles natively.</p>
                    <input
                      type="url"
                      placeholder="https://your-service.example.com/webhooks/billing"
                      value={webhookConfig.endpointUrl}
                      onChange={e => setWebhookConfig(c => ({ ...c, endpointUrl: e.target.value }))}
                      className="w-full px-3 py-2 text-sm rounded bg-slate-50 border border-slate-300 text-slate-900 placeholder-slate-400"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Push pattern — webhook config */}
            {pattern === 'push' && authSystem !== 'rule42-auth' && (
              <div className="space-y-4">
                <div className="p-4 rounded-lg border border-slate-200 bg-white space-y-3">
                  <h3 className="text-sm font-semibold text-slate-900">Webhook Endpoint</h3>
                  <p className="text-xs text-slate-600">Your server endpoint that will receive billing event webhooks.</p>
                  <input
                    type="url"
                    placeholder="https://api.yourapp.com/webhooks/billing"
                    value={webhookConfig.endpointUrl}
                    onChange={e => setWebhookConfig(c => ({ ...c, endpointUrl: e.target.value }))}
                    className="w-full px-3 py-2 text-sm rounded bg-slate-50 border border-slate-300 text-slate-900 placeholder-slate-400"
                  />
                </div>

                <div className="p-4 rounded-lg border border-slate-200 bg-white space-y-3">
                  <h3 className="text-sm font-semibold text-slate-900">Webhook Secret (Optional)</h3>
                  <p className="text-xs text-slate-600">Used to verify webhook signatures. We'll include an HMAC-SHA256 signature in the <code className="text-indigo-600">X-Billing-Signature</code> header.</p>
                  <input
                    type="text"
                    placeholder="whsec_..."
                    value={webhookConfig.secret}
                    onChange={e => setWebhookConfig(c => ({ ...c, secret: e.target.value }))}
                    className="w-full px-3 py-2 text-sm rounded bg-slate-50 border border-slate-300 text-slate-900 placeholder-slate-400"
                  />
                </div>

                <div className="p-4 rounded-lg border border-slate-200 bg-white space-y-3">
                  <h3 className="text-sm font-semibold text-slate-900">Events to Subscribe</h3>
                  <p className="text-xs text-slate-600">Select which billing events should trigger webhooks.</p>
                  <div className="grid grid-cols-2 gap-2">
                    {WEBHOOK_EVENTS.map(evt => (
                      <label key={evt} className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={webhookConfig.events.includes(evt)}
                          onChange={e => {
                            setWebhookConfig(c => ({
                              ...c,
                              events: e.target.checked
                                ? [...c.events, evt]
                                : c.events.filter(x => x !== evt),
                            }));
                          }}
                          className="rounded border-slate-300 bg-slate-50 text-indigo-500"
                        />
                        {TRIGGER_LABELS[evt] ?? evt}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Code example for receiving webhooks */}
                <div className="p-4 rounded-lg border border-slate-200 bg-white space-y-2">
                  <h3 className="text-sm font-semibold text-slate-900">Receiver Example (Express.js)</h3>
                  <pre className="text-[11px] text-slate-700 bg-slate-50 p-3 rounded overflow-x-auto whitespace-pre">
{authSystem === 'auth0' ? `// Auth0 Action — Post-Login
exports.onExecutePostLogin = async (event, api) => {
  // Call billing API to check entitlements
  const res = await fetch(
    'https://finance.rule42.ai/entitlements/check' +
    '?customerId=' + event.user.user_id +
    '&slug=pro-access',
    { headers: { Authorization: 'Bearer ' + YOUR_BILLING_TOKEN } }
  );
  const { entitled } = await res.json();

  if (entitled) {
    api.accessToken.setCustomClaim('plan_tier', 'pro');
    api.idToken.setCustomClaim('plan_tier', 'pro');
  }
};` : authSystem === 'okta' ? `// Okta Inline Hook Handler
app.post('/hooks/okta/token-inline', (req, res) => {
  const userId = req.body.data?.context?.user?.id;

  // Call billing API to check entitlements
  const entitlements = await fetch(
    'https://finance.rule42.ai/entitlements/customer/' + userId,
    { headers: { Authorization: 'Bearer ' + BILLING_TOKEN } }
  );
  const { entitlements: list } = await entitlements.json();
  const slugs = list.map(e => e.slug);

  res.json({
    commands: [{
      type: 'com.okta.access.patch',
      value: [{ op: 'add', path: '/claims/entitlements', value: slugs }]
    }]
  });
});` : `// Express.js Webhook Receiver
app.post('/webhooks/billing', express.json(), (req, res) => {
  const { event, data } = req.body;

  switch (event) {
    case 'subscription.created':
      // Grant access to the user
      grantAccess(data.customerId, data.planId);
      break;
    case 'subscription.canceled':
      // Revoke access
      revokeAccess(data.customerId);
      break;
    case 'payment.failed':
      // Restrict access or notify
      restrictAccess(data.customerId);
      break;
  }

  res.json({ received: true });
});`}
                  </pre>
                </div>
              </div>
            )}

            {/* Pull pattern — API config */}
            {pattern === 'pull' && (
              <div className="space-y-4">
                <div className="p-4 rounded-lg border border-indigo-500/30 bg-indigo-500/5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-lg">ℹ️</span>
                    <span className="font-semibold text-indigo-600">API-Based Enforcement</span>
                  </div>
                  <p className="text-xs text-slate-600">Your application checks entitlements via the billing API. No incoming webhooks needed — your app pulls entitlement status on demand.</p>
                </div>

                <div className="p-4 rounded-lg border border-slate-200 bg-white space-y-3">
                  <h3 className="text-sm font-semibold text-slate-900">API Endpoint</h3>
                  <p className="text-xs text-slate-600">Use this base URL in your application to check entitlements:</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 px-3 py-2 text-xs rounded bg-slate-50 border border-slate-300 text-indigo-600">{base}/entitlements/check?customerId=CID&slug=SLUG</code>
                    <button
                      onClick={() => void navigator.clipboard.writeText(`${base}/entitlements/check?customerId=CID&slug=SLUG`)}
                      className="px-2 py-2 text-xs rounded bg-slate-200 text-slate-700 hover:bg-slate-300"
                    >
                      📋
                    </button>
                  </div>
                </div>

                {/* Client-side code example */}
                <div className="p-4 rounded-lg border border-slate-200 bg-white space-y-2">
                  <h3 className="text-sm font-semibold text-slate-900">Integration Examples</h3>

                  <div className="mt-2">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Express Middleware</div>
                    <pre className="text-[11px] text-slate-700 bg-slate-50 p-3 rounded overflow-x-auto whitespace-pre">{`async function requireEntitlement(slug) {
  return async (req, res, next) => {
    const customerId = req.user.billingCustomerId;
    const resp = await fetch(
      '${base}/entitlements/check' +
      '?customerId=' + customerId +
      '&slug=' + slug,
      { headers: { Authorization: 'Bearer ' + BILLING_TOKEN } }
    );
    const { entitled } = await resp.json();
    if (!entitled) return res.status(403).json({ error: 'Upgrade required' });
    next();
  };
}

// Usage:
app.get('/api/pro-feature', requireEntitlement('pro-access'), handler);`}</pre>
                  </div>

                  <div className="mt-3">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">React Hook</div>
                    <pre className="text-[11px] text-slate-700 bg-slate-50 p-3 rounded overflow-x-auto whitespace-pre">{`function useEntitlement(slug) {
  const [entitled, setEntitled] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/entitlements/check?slug=' + slug)
      .then(r => r.json())
      .then(d => { setEntitled(d.entitled); setLoading(false); });
  }, [slug]);

  return { entitled, loading };
}

// Usage:
function ProFeature() {
  const { entitled, loading } = useEntitlement('pro-access');
  if (loading) return <Spinner />;
  if (!entitled) return <UpgradePrompt />;
  return <ProContent />;
}`}</pre>
                  </div>
                </div>
              </div>
            )}

            {/* Passive pattern */}
            {pattern === 'passive' && (
              <div className="space-y-4">
                <div className="p-4 rounded-lg border border-amber-500/30 bg-amber-500/5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-lg">📬</span>
                    <span className="font-semibold text-amber-600">Notification-Based Enforcement</span>
                  </div>
                  <p className="text-xs text-slate-600">Automation rules will send emails or external notifications when billing events occur. You handle access changes manually based on these notifications.</p>
                </div>

                <div className="p-4 rounded-lg border border-slate-200 bg-white space-y-3">
                  <h3 className="text-sm font-semibold text-slate-900">Notification Channel</h3>
                  <p className="text-xs text-slate-600">Where should billing notifications be sent?</p>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-xs text-slate-700">
                      <input type="radio" name="channel" defaultChecked className="text-indigo-500" />
                      Email (admin email on file)
                    </label>
                    <label className="flex items-center gap-2 text-xs text-slate-700">
                      <input type="radio" name="channel" className="text-indigo-500" />
                      External webhook (Slack, Discord, etc.)
                    </label>
                  </div>
                </div>

                {/* External notification URL */}
                <div className="p-4 rounded-lg border border-slate-200 bg-white space-y-3">
                  <h3 className="text-sm font-semibold text-slate-900">External Notification URL (Optional)</h3>
                  <p className="text-xs text-slate-600">A Slack incoming webhook, Discord webhook, or any HTTP endpoint for notifications.</p>
                  <input
                    type="url"
                    placeholder="https://hooks.slack.com/services/T00/B00/xxxx"
                    value={webhookConfig.endpointUrl}
                    onChange={e => setWebhookConfig(c => ({ ...c, endpointUrl: e.target.value }))}
                    className="w-full px-3 py-2 text-sm rounded bg-slate-50 border border-slate-300 text-slate-900 placeholder-slate-400"
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Step 4: Automation Rules ── */}
        {step === 'rules' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-bold text-slate-900">Suggested Automation Rules</h2>
              <p className="text-sm text-slate-600 mt-1">Based on your {pattern} enforcement pattern, we recommend these automation rules. Select the ones you'd like to create.</p>
            </div>

            <div className="space-y-2">
              {suggestedRules.map((rule, i) => (
                <button
                  key={i}
                  onClick={() => toggleRule(i)}
                  className={`w-full flex items-start gap-3 p-4 rounded-lg border text-left transition-all ${
                    rule.selected
                      ? 'border-indigo-500 bg-indigo-500/10'
                      : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                >
                  <div className={`mt-0.5 w-5 h-5 rounded flex items-center justify-center text-xs ${
                    rule.selected ? 'bg-indigo-600 text-white' : 'bg-slate-200 text-slate-600'
                  }`}>
                    {rule.selected ? '✓' : ''}
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-slate-900 text-sm">{rule.name}</div>
                    <div className="text-xs text-slate-600 mt-0.5">{rule.description}</div>
                    <div className="flex items-center gap-3 mt-2">
                      <span className="text-[10px] px-2 py-0.5 rounded bg-blue-500/20 text-blue-600">{TRIGGER_LABELS[rule.trigger] ?? rule.trigger}</span>
                      <span className="text-[10px] text-slate-500">→</span>
                      {rule.delay > 0 && <span className="text-[10px] px-2 py-0.5 rounded bg-amber-500/20 text-amber-600">{rule.delay}d delay</span>}
                      <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-600">{rule.action.replace(/_/g, ' ')}</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {createdCount > 0 && (
              <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-sm text-emerald-600">
                ✅ Created {createdCount} automation rule{createdCount > 1 ? 's' : ''}. You can manage them on the Automation Rules page.
              </div>
            )}

            {createdCount === 0 && (
              <button
                onClick={() => void handleCreateRules()}
                disabled={creatingRules || suggestedRules.filter(r => r.selected).length === 0}
                className="w-full py-3 text-sm font-medium rounded-lg bg-indigo-600 text-white hover:bg-indigo-500 disabled:opacity-50"
              >
                {creatingRules ? 'Creating rules…' : `Create ${suggestedRules.filter(r => r.selected).length} Rule${suggestedRules.filter(r => r.selected).length !== 1 ? 's' : ''}`}
              </button>
            )}
          </div>
        )}

        {/* ── Step 5: Test & Verify ── */}
        {step === 'test' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-bold text-slate-900">Verify Your Setup</h2>
              <p className="text-sm text-slate-600 mt-1">Run these checks to make sure everything is connected properly.</p>
            </div>

            {/* Checklist */}
            <div className="space-y-3">
              <div className="p-4 rounded-lg border border-slate-200 bg-white">
                <div className="flex items-center gap-3">
                  <span className="text-lg">{createdCount > 0 ? '✅' : '⬜'}</span>
                  <div>
                    <div className="text-sm font-semibold text-slate-900">Automation Rules Created</div>
                    <div className="text-xs text-slate-600">{createdCount > 0 ? `${createdCount} rules active` : 'Go back and create rules'}</div>
                  </div>
                </div>
              </div>

              {authSystem === 'rule42-auth' && (
                <div className="p-4 rounded-lg border border-slate-200 bg-white">
                  <div className="flex items-center gap-3">
                    <span className="text-lg">{syncResult ? '✅' : '⬜'}</span>
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-slate-900">Auth Roles Synced</div>
                      <div className="text-xs text-slate-600">{syncResult ? `${syncResult.created + syncResult.updated} roles synced` : 'Sync business roles from rule42-auth'}</div>
                    </div>
                    {!syncResult && (
                      <button
                        onClick={() => void handleSyncFromAuth()}
                        disabled={syncing}
                        className="px-3 py-1.5 text-xs rounded bg-indigo-600 text-white hover:bg-indigo-500 disabled:opacity-50"
                      >
                        {syncing ? 'Syncing…' : 'Sync Now'}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {pattern === 'push' && webhookConfig.endpointUrl && (
                <div className="p-4 rounded-lg border border-slate-200 bg-white">
                  <div className="flex items-center gap-3">
                    <span className="text-lg">{testStatus === 'success' ? '✅' : testStatus === 'error' ? '❌' : '⬜'}</span>
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-slate-900">Webhook Connectivity</div>
                      <div className="text-xs text-slate-600">
                        {testStatus === 'success' ? testMessage
                         : testStatus === 'error' ? testMessage
                         : `Test connection to ${webhookConfig.endpointUrl}`}
                      </div>
                    </div>
                    <button
                      onClick={() => void handleTestWebhook()}
                      disabled={testStatus === 'testing'}
                      className="px-3 py-1.5 text-xs rounded bg-indigo-600 text-white hover:bg-indigo-500 disabled:opacity-50"
                    >
                      {testStatus === 'testing' ? 'Testing…' : 'Test'}
                    </button>
                  </div>
                </div>
              )}

              {pattern === 'pull' && (
                <div className="p-4 rounded-lg border border-slate-200 bg-white">
                  <div className="flex items-center gap-3">
                    <span className="text-lg">ℹ️</span>
                    <div>
                      <div className="text-sm font-semibold text-slate-900">API Integration</div>
                      <div className="text-xs text-slate-600">Use the entitlements API from your application code. See the Integration Docs page for full API reference.</div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Next steps summary */}
            <div className="p-4 rounded-lg border border-slate-200 bg-white">
              <h3 className="text-sm font-semibold text-slate-900 mb-3">Next Steps</h3>
              <ol className="list-decimal list-inside space-y-2 text-xs text-slate-600">
                {authSystem === 'rule42-auth' && (
                  <>
                    <li>Open <strong className="text-slate-900">Plans</strong> and assign entitlements to each plan tier</li>
                    <li>Verify business roles are synced from auth (check the Entitlements section in plan detail)</li>
                    <li>Test by creating a test subscription and checking role assignment in rule42-auth</li>
                  </>
                )}
                {pattern === 'push' && authSystem !== 'rule42-auth' && (
                  <>
                    <li>Deploy the webhook receiver code to your server</li>
                    <li>Implement access grant/revoke logic for each event type</li>
                    <li>Create a test subscription to verify the webhook fires</li>
                    <li>Monitor the <strong className="text-slate-900">Automation Rules</strong> page for execution history</li>
                  </>
                )}
                {pattern === 'pull' && (
                  <>
                    <li>Add the entitlement check middleware to your API routes</li>
                    <li>Create a billing customer for each user at registration</li>
                    <li>Map entitlements to plans in the <strong className="text-slate-900">Plans</strong> page</li>
                    <li>Test by subscribing a customer and checking API access</li>
                  </>
                )}
                {pattern === 'passive' && (
                  <>
                    <li>Set up email templates for each billing event type</li>
                    <li>Configure your notification channel (email or webhook)</li>
                    <li>When you receive a notification, manually update access in your system</li>
                    <li>Consider upgrading to Push or Pull enforcement as you scale</li>
                  </>
                )}
              </ol>
            </div>

            {/* Complete button */}
            <a
              href="/automation-rules"
              className="block w-full py-3 text-center text-sm font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-500"
            >
              Go to Automation Rules →
            </a>
          </div>
        )}

        {/* ── Navigation ── */}
        <div className="flex items-center justify-between pt-4 border-t border-slate-200">
          <button
            onClick={goBack}
            disabled={stepIndex === 0}
            className="px-4 py-2 text-sm rounded bg-slate-200 text-slate-700 hover:bg-slate-300 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            ← Back
          </button>
          {stepIndex < steps.length - 1 && (
            <button
              onClick={goNext}
              disabled={!canProceed}
              className="px-6 py-2 text-sm font-medium rounded bg-indigo-600 text-white hover:bg-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Continue →
            </button>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
